import { Fragment, useEffect, useMemo, useRef, useState } from "react"

// ** Third Party Components
import { useQuery } from "@tanstack/react-query"
import { Add, CloseCircle } from "iconsax-reactjs"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

// ** Theme Components
import PopoverDialog from "./PopoverDialog"
import { Drawer, DrawerContent } from "@pma/components/common/drawer"

// ** Constants
import { menuItems } from "@pma/utils/constants"
import { USER_PAGE_ROUTE } from "@pma/utils/page-routes"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Store
import { useWorkDiary } from "@pma/stores/tasks-store"
import { useCommonStore } from "@pma/stores/common-store"

// ** Services
import { getProjectGroupService } from "@pma/services/project"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"
import { usePermissions } from "@pma/hooks/use-permissions"

// ** Custom Components
import NewFolder from "@pma/components/File-Folder/New-Folder"
import TaskInsertDialog from "@pma/components/Task/TaskInsert"
import UploadFiles from "@pma/components/File-Folder/Upload-Files"
import ProjectGroupDialog from "@pma/components/Project/ProjectGroup"
import ProjectInsertUpdateDialog from "@pma/components/Project/InsertUpdate"
import { showDeletePermissionToast } from "@pma/pages/Project/Task/Common/DeletePermissionToast"

const QuickActions = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()
  const { setWorkDiaryDialog } = useWorkDiary()
  const { setProjectGroupListListAction } = useCommonStore()

  const isHebrew = locale === "he"
  const containerRef = useRef<HTMLDivElement | null>(null)
  const activeView = searchParam.get("view") == "work-diary" ? true : false
  const { isOrgAdmin: isAdmin, organization_id } = usePermissions()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showDrawer, setShowDrawer] = useState<boolean>(false)
  const [hoverItem, setHoverItem] = useState<string | null>(null)
  const [activeItem, setActiveItem] = useState<string | null>(null)
  const [mobileActiveItem, setMobileActiveItem] = useState<string | null>(null)

  // Check if current route is files route
  const isFilesRoute = location.pathname.includes("files")

  // Check if current route is finance route
  const isFinanceRoute = location.pathname.includes("finance")
  const financeTab = searchParam.get("tab")

  // Check if current route is users list route (exact match, not user detail)
  const isUsersListRoute =
    location.pathname === USER_PAGE_ROUTE || location.pathname === `${USER_PAGE_ROUTE}/`

  // Get button text based on route
  const getButtonText = () => {
    if (activeView) return "Add Work Diaries"
    if (isFinanceRoute) {
      if (financeTab === "contractors") return "Add Contractor"
      return "Create Estimate"
    }
    if (isUsersListRoute) return "Add New User"
    if (isFilesRoute) return "Create or upload"
    return "Add New"
  }
  const buttonText = getButtonText()

  // Helper to determine if icon should be bold (hover or active/clicked)
  const isItemBold = (item: string) => hoverItem === item || activeItem === item

  const { data, refetch: refetchProjectGroupList } = useQuery({
    queryKey: ["get-project-group-list"],
    queryFn: () => getProjectGroupService({ organization_id }),
  })

  useEffect(() => {
    if (data) setProjectGroupListListAction(data)
  }, [data])

  // Handle item click for desktop
  const handleItemClick = (item: string) => {
    setIsDropdownOpen(false)
    setActiveItem(item)
    setHoverItem(null)
  }

  // Handle close mobile drawer content
  const handleMobileClose = () => {
    setHoverItem(null)
    setMobileActiveItem(null)
    setShowDrawer(false)
  }

  // Render mobile drawer content based on active item
  const renderMobileDrawerContent = () => {
    switch (mobileActiveItem) {
      case "newFolder":
        return <NewFolder onClose={handleMobileClose} isMobile={true} />
      case "uploadFolder":
        return null
      case "uploadFile":
        return <UploadFiles onClose={handleMobileClose} isMobile={true} />
      case "project":
        return <ProjectInsertUpdateDialog onClose={handleMobileClose} isMobile={true} />
      case "task":
        return <TaskInsertDialog onClose={closePopover} isMobile={true} />
      case "group":
        return (
          <ProjectGroupDialog
            onClose={handleMobileClose}
            isMobile={true}
            refetchProjectGroupList={refetchProjectGroupList}
          />
        )
      default:
        return null
    }
  }

  // Get title for mobile drawer based on active item
  const getMobileDrawerTitle = () => {
    switch (mobileActiveItem) {
      case "newFolder":
        return translate("New Folder")
      case "uploadFolder":
        return translate("Upload Folder")
      case "uploadFile":
        return (
          <>
            {translate("Folder")} \ {translate("File Upload")}
          </>
        )
      case "group":
        return translate("New Group")
      case "task":
        return translate("New Task")
      case "file":
        return translate("New File")
      default:
        return translate(buttonText)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Reset mobile active item when drawer closes
  useEffect(() => {
    if (!showDrawer) {
      setMobileActiveItem(null)
      setHoverItem(null)
    }
  }, [showDrawer])

  const filteredMenuItems = useMemo(() => {
    if (isFilesRoute)
      return menuItems.filter((item) =>
        ["newFolder", "uploadFolder", "uploadFile"].includes(item.id),
      )
    else return menuItems.filter((item) => ["project", "group", "task"].includes(item.id))
  }, [isFilesRoute])

  const closePopover = () => {
    setActiveItem(null)
    setHoverItem(null)
  }

  const handleAddUserClick = () => {
    if (!isAdmin) {
      showDeletePermissionToast({
        message: translate("Only project managers can add users."),
      })
      return
    }
    navigate("?add-user=true")
  }

  const renderPopoverContent = () => {
    switch (activeItem) {
      case "newFolder":
        return (
          <PopoverDialog isOpen={true} onClose={closePopover} className='w-max md:w-[650px]'>
            <NewFolder onClose={closePopover} />
          </PopoverDialog>
        )
      case "uploadFolder":
        return null
      case "uploadFile":
        return (
          <PopoverDialog isOpen={true} onClose={closePopover} className='w-max md:w-[650px]'>
            <UploadFiles onClose={closePopover} />
          </PopoverDialog>
        )
      case "project":
        return (
          <PopoverDialog isOpen={true} onClose={closePopover} className='w-max md:w-[650px]'>
            <ProjectInsertUpdateDialog onClose={closePopover} />
          </PopoverDialog>
        )
      case "task":
        return (
          <PopoverDialog isOpen={true} onClose={closePopover} className='w-max md:w-[650px]'>
            <TaskInsertDialog onClose={closePopover} />
          </PopoverDialog>
        )
      case "group":
        return (
          <PopoverDialog isOpen={true} onClose={closePopover} className='w-max md:w-[650px]'>
            <ProjectGroupDialog
              onClose={closePopover}
              refetchProjectGroupList={refetchProjectGroupList}
            />
          </PopoverDialog>
        )
      default:
        return null
    }
  }

  return (
    <Fragment>
      {/* Mobile Button */}
      <button
        className='flex items-center justify-center gap-1 rounded-full bg-[radial-gradient(109.91%_126.55%_at_13.06%_22.92%,_#7A5AF8_30.95%,_#6938EF_65.87%)] px-2.5 py-1.5 text-white shadow-md transition-all hover:opacity-90 active:scale-95 xs:gap-1.5 xs:px-3 xs:py-2 md:hidden'
        onClick={() => {
          if (activeView) setWorkDiaryDialog(true)
          else if (isFinanceRoute) {
            if (financeTab === "contractors") navigate("?tab=contractors&add-contractor=true")
            else navigate("?create-estimate=true")
          }
          else if (isUsersListRoute) handleAddUserClick()
          else setShowDrawer(true)
        }}
      >
        <Add size={14} className='shrink-0' />
        <span className='whitespace-nowrap font-[inter] text-[11px] font-semibold xs:text-xs'>
          {translate(buttonText)}
        </span>
      </button>

      {/* Desktop Dropdown */}
      <div className='relative hidden md:inline-block' ref={containerRef}>
        <button
          onClick={() => {
            if (activeView) setWorkDiaryDialog(true)
            else if (isFinanceRoute) {
              if (financeTab === "contractors") navigate("?tab=contractors&add-contractor=true")
              else navigate("?create-estimate=true")
            }
            else if (isUsersListRoute) handleAddUserClick()
            else {
              if (activeItem) setActiveItem(null)
              else setIsDropdownOpen(!isDropdownOpen)
            }
          }}
          className='flex items-center gap-2 rounded-full px-4 py-3 text-white shadow-sm hover:opacity-90'
          style={
            isDropdownOpen
              ? {
                  background:
                    "radial-gradient(109.91% 126.55% at 13.06% 22.92%, #6938EF 30.95%, #5925DC 65.87%)"
                }
              : {
                  background:
                    "radial-gradient(109.76% 151.9% at 7.34% 35%, #9B8AFB 0%, #7F6AFF 44.27%)",
                }
          }
        >
          <Add size={16} />
          <span className='font-[inter] text-sm font-bold'>{translate(buttonText)}</span>
        </button>

        {isDropdownOpen && !activeItem && (
          <div
            className={cn(
              "absolute z-40 mt-2 w-48 rounded-xl bg-white p-2 shadow-lg",
              isHebrew ? "right-0" : "left-0",
            )}
          >
            <div className='border-b border-b-[#E5E5EC] p-2'>
              <h5 className='font-[inter] text-sm font-normal leading-6 text-neutral-600'>
                {translate("Add new")}
              </h5>
            </div>
            {filteredMenuItems.map((menu) => (
              <button
                key={menu.id}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 hover:bg-[#E0EAFF] hover:text-neutral-800",
                  isHebrew ? "pr-2" : "pl-2",
                )}
                onMouseEnter={() => setHoverItem(menu.id)}
                onMouseLeave={() => setHoverItem(null)}
                onClick={() => handleItemClick(menu.id)}
              >
                <menu.icon size='24' variant={isItemBold(menu.id) ? "Bold" : "Linear"} />
                <span className='font-[inter] text-sm font-normal leading-6 text-neutral-800'>
                  {translate(menu.label)}
                </span>
              </button>
            ))}
          </div>
        )}

        {activeItem && renderPopoverContent()}
      </div>

      {/* Mobile Drawer */}
      <Drawer open={showDrawer} onOpenChange={setShowDrawer}>
        <DrawerContent className={isHebrew ? "rtl" : "ltr"}>
          <div className='flex flex-col p-2'>
            <div className='flex gap-4 border-b border-neutral-200 px-4 py-2'>
              <div className='flex w-full items-center justify-between'>
                <h2 className='font-[inter] text-lg font-normal text-neutral-800'>
                  {getMobileDrawerTitle()}
                </h2>
                <CloseCircle
                  className='cursor-pointer text-neutral-800'
                  size={24}
                  onClick={handleMobileClose}
                />
              </div>
            </div>

            {/* Content area */}
            <div className={cn("flex flex-col gap-1 pt-2", isHebrew ? "pr-2" : "pl-2")}>
              {mobileActiveItem ? (
                <div>{renderMobileDrawerContent()}</div>
              ) : (
                filteredMenuItems.map((menu) => (
                  <div className={cn("flex py-1", isHebrew ? "pr-3.5" : "pl-3.5")}>
                    <div
                      className='flex cursor-pointer items-center gap-2'
                      onMouseEnter={() => setHoverItem(menu.id)}
                      onMouseLeave={() => setHoverItem(null)}
                      onClick={() => setMobileActiveItem(menu.id)}
                    >
                      <menu.icon size='24' variant={isItemBold(menu.id) ? "Bold" : "Linear"} />
                      <p className='font-[inter] text-sm font-normal text-neutral-800'>
                        {translate(menu.label)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </Fragment>
  )
}

export default QuickActions
